/**
 * Supabase localStorage utilities
 * Helper functions to access Supabase session data from localStorage
 *
 * CRITICAL: Use these instead of getSession() to prevent hangs when tab is inactive
 */

/**
 * Get Supabase auth storage key dynamically
 * Supabase stores session in localStorage with pattern: sb-{projectRef}-auth-token
 */
export function getSupabaseAuthKey(): string | null {
  // Try to find the Supabase auth key in localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
      return key;
    }
  }
  return null;
}

/**
 * Get Supabase session data from localStorage
 * Returns null if no session exists
 */
export function getSupabaseSession(): any | null {
  try {
    const authKey = getSupabaseAuthKey();
    if (!authKey) {
      return null;
    }

    const supabaseAuth = localStorage.getItem(authKey);
    if (!supabaseAuth) {
      return null;
    }

    return JSON.parse(supabaseAuth);
  } catch (error) {
    console.error('[SupabaseStorage] Error getting session:', error);
    return null;
  }
}

/**
 * Get access token from localStorage
 * Returns null if no token exists
 *
 * ✅ MOBILE WEBVIEW SUPPORT: Also checks for injected session from mobile app
 */
export function getAccessToken(): string | null {
  // First, check for mobile WebView injected session object (highest priority)
  if (typeof window !== 'undefined' && (window as any).__MOBILE_SUPABASE_SESSION__?.access_token) {
    console.log('[SupabaseStorage] Using mobile WebView injected session');
    return (window as any).__MOBILE_SUPABASE_SESSION__.access_token;
  }

  // Fallback to localStorage session
  const session = getSupabaseSession();
  return session?.access_token ?? null;
}

/**
 * Get user ID from localStorage
 * Returns null if no user exists
 */
export function getUserId(): string | null {
  const session = getSupabaseSession();
  return session?.user?.id ?? null;
}

/**
 * Check if session exists in localStorage
 */
export function hasSession(): boolean {
  return getSupabaseSession() !== null;
}
